import React, { useCallback, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { DndContext, DragOverlay } from '@dnd-kit/core';
import { BlockRenderer } from './renderer';
import { useDragAndDrop } from './hooks/useDragAndDrop';
import { useBlockHandlers } from './hooks/useBlockHandlers';
import { useEditorSelection } from './hooks/useEditorSelection';
import { useEditorPaste } from './hooks/useEditorPaste';
import { useSmoothWheel } from './hooks/useSmoothWheel';
import { useDocImageUpload } from './hooks/useDocImageUpload';
import { useDocVideoUpload } from './hooks/useDocVideoUpload';
import { useToolbarSlot } from './hooks/useToolbarSlot';
import { useDocEditorUndo } from './hooks/useDocEditorUndo';
import { useEditorScrollChrome } from './hooks/useEditorScrollChrome';
import { useInlineCodeShortcut } from './hooks/useInlineCodeShortcut';
import { useLineStartKeys } from './hooks/useLineStartKeys';
import { useDocFind } from './hooks/useDocFind';
import { useMediaFileDrop } from './hooks/useMediaFileDrop';
import { useDocEditorVariables } from './hooks/useDocEditorVariables';
import { BottomDropSpace } from './components/BottomDropSpace';
import { DocEditorToolbar } from './components/DocEditorToolbar';
import { DocFindBar } from './components/DocFindBar';
import { DocMinimap } from './components/DocMinimap';
import { BlockDragOverlay } from './components/BlockDragOverlay';
import { createBlock } from './utils/blockUtils';
import { getNumberedListIndex } from './utils/indentUtils';
import { toggleInlineCode } from './utils/inlineCode';
import { snapOverlayCenterToCursor } from './utils/snapOverlayToCursor';
import { clearSnippetBacklinksForSourceBlocks } from '../../utils/snippetBacklinks';
import ConfirmModal from '../SprintDetail/FlowchartView/components/ConfirmModal';
import './styles/doc-editor.css';

/**
 * DocEditor — the block-based document editor for Docs.
 *
 * Controlled component: the parent owns the `blocks` array (block-tree JSON)
 * and passes `onChange`.
 *
 * STALENESS GUARD (important): react-contenteditable skips re-rendering when
 * only event handlers change, so a block you haven't typed in keeps handlers
 * bound at an OLD render. If those handlers closed over an old `blocks`
 * tree, pressing Enter there would rebuild the document from a stale
 * snapshot (blocks jumping back to old positions, new blocks vanishing).
 * Therefore every handler here is identity-stable (created once) and reads
 * the CURRENT tree through `blocksRef` — a stale-bound handler is still the
 * same function and still sees the live document. `commit` also updates the
 * ref synchronously so sequential edits within one event compose correctly.
 */
export const DocEditor = ({
  blocks,
  onChange,
  docId,
  onOpenDoc,
  resolveDocMeta,
  availableDocs,
  /** { [sourceBlockId]: [{ consumerDocId, snippetBlockId }] } for this doc */
  snippetBacklinks,
  /**
   * Variables (doc scope + campaign chain, highest level wins):
   * `resolveVariable(name)` → { value, color, sourceName } | null — host must
   * memoize it on its variables data (pills refresh when the identity changes).
   * `availableVariables` feeds the "{{" picker; `onCreateVariable(name)`
   * registers a new (doc-scoped) variable typed in the picker.
   */
  resolveVariable,
  availableVariables,
  onCreateVariable,
  /**
   * In-doc Variables block edits host-owned `doc.variables` in the library.
   * Through a campaign, `onCampaignVariablesChange` owns value overrides.
   * Through a mission, `onMissionVariablesChange` owns them instead.
   * New names stay on `doc.variables`.
   */
  docVariables,
  onDocVariablesChange,
  variableCampaign,
  allSprints,
  onCampaignVariablesChange,
  missionVariables,
  onMissionVariablesChange,
  onPatchInheritedVariable,
  variablesColumnWidths,
  onVariablesColumnWidthsChange,
  /** Mission run-state: per-block tick overrides keyed by checkbox block id */
  tickOverrides,
  /** When set, checkbox ticks update run-state without rewriting the block tree */
  onTickChange,
  /** Replace the full override map (Uncheck all + undo/redo of that action) */
  onTickOverridesChange,
  /** Per-mission file-upload values: { [blockId]: FileRef[] } */
  fileOverrides,
  /** When set, upload-field values update run-state without rewriting the block tree */
  onFilesChange,
  /** Mission this pane is opened from — required for upload/download */
  missionId,
}) => {
  const editorRootRef = useRef(null);
  const toolbarSlot = useToolbarSlot(editorRootRef);

  const {
    blocksRef,
    commit,
    commitDocVariables,
    commitDocVariablesAndBlocks,
    docVariablesRef,
    variablesHistoryEpoch,
    handleUpdate,
    handleInsertAfter,
    handleUpdateAndInsert,
    handleDelete: rawHandleDelete,
    handleMergeWithPrevious,
    handleMergeWithNext,
    handleEscapeContainer,
    handleWrapInCallout,
    handleUnwrapCallout,
    handleNavigate,
    handleCanvasMouseDown,
    handleCanvasClick,
    uncheckAll,
    undo,
    redo,
  } = useBlockHandlers(blocks, onChange, {
    docVariables,
    onDocVariablesChange,
    tickOverrides,
    onTickOverridesChange,
  });

  const {
    handleDocVariablesChange,
    handleCreateVariable,
    handleRenameVariable,
    emptyVariableValues,
    anyChecked,
    canEmptyVariableValues,
    hasVariablesBlock,
  } = useDocEditorVariables({
    blocks,
    tickOverrides,
    commitDocVariables,
    commitDocVariablesAndBlocks,
    docVariables,
    docVariablesRef,
    blocksRef,
    undo,
    onDocVariablesChange,
    onCreateVariable,
    onCampaignVariablesChange,
    variableCampaign,
    allSprints,
    missionVariables,
    onMissionVariablesChange,
  });

  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const deleteConfirmResolverRef = useRef(null);

  const beforeDeleteBlocks = useCallback(
    (ids) => {
      if (!docId || !snippetBacklinks || !ids?.length) return true;
      const hits = ids
        .map((id) => ({
          id,
          links: snippetBacklinks[id] || [],
        }))
        .filter((h) => h.links.length > 0);
      if (!hits.length) return true;

      const consumerIds = new Set();
      hits.forEach((h) => h.links.forEach((l) => consumerIds.add(l.consumerDocId)));
      const names = [...consumerIds].map(
        (id) => resolveDocMeta?.(id)?.title || 'Untitled Doc'
      );
      const uniqueNames = [...new Set(names)];
      const list =
        uniqueNames.length <= 3
          ? uniqueNames.join(', ')
          : `${uniqueNames.slice(0, 3).join(', ')} and ${uniqueNames.length - 3} more`;

      return new Promise((resolve) => {
        deleteConfirmResolverRef.current = resolve;
        setDeleteConfirm({
          ids,
          title:
            hits.length === 1
              ? 'Delete referenced block?'
              : 'Delete referenced blocks?',
          message: `This is embedded as a snippet in ${
            uniqueNames.length === 1 ? 'another doc' : `${uniqueNames.length} docs`
          } (${list}). Delete anyway? Those embeds will show as broken.`,
        });
      });
    },
    [docId, snippetBacklinks, resolveDocMeta]
  );

  const finishDeleteConfirm = useCallback(
    (confirmed) => {
      const resolve = deleteConfirmResolverRef.current;
      deleteConfirmResolverRef.current = null;
      const ids = deleteConfirm?.ids;
      setDeleteConfirm(null);
      if (confirmed && docId && ids?.length) {
        clearSnippetBacklinksForSourceBlocks(docId, ids).catch(() => {});
      }
      resolve?.(confirmed);
    },
    [deleteConfirm, docId]
  );

  const handleDelete = useCallback(
    (blockId) => {
      Promise.resolve(beforeDeleteBlocks([blockId])).then((ok) => {
        if (ok !== false) rawHandleDelete(blockId);
      });
    },
    [beforeDeleteBlocks, rawHandleDelete]
  );

  const { selectedBlockId, setSelectedBlockId, multiSelected, setMultiSelected } =
    useEditorSelection(blocksRef, commit, editorRootRef, {
      beforeDeleteBlocks,
    });

  const onWrapInCallout = useCallback(
    (blockId, opts) => {
      const calloutId = handleWrapInCallout(blockId, opts);
      if (!calloutId) return;
      setMultiSelected([]);
      setSelectedBlockId(calloutId);
    },
    [handleWrapInCallout, setMultiSelected, setSelectedBlockId]
  );

  const onUnwrapCallout = useCallback(
    (calloutId) => {
      const focusId = handleUnwrapCallout(calloutId);
      setMultiSelected([]);
      setSelectedBlockId(null);
      return focusId;
    },
    [handleUnwrapCallout, setMultiSelected, setSelectedBlockId]
  );

  const videoPasteChoice = useEditorPaste(
    blocksRef,
    commit,
    editorRootRef,
    selectedBlockId,
    setSelectedBlockId
  );
  useDocImageUpload(docId, blocksRef, commit);
  useDocVideoUpload(docId, blocksRef, commit);

  const undoArmedRef = useDocEditorUndo(editorRootRef, undo, redo);
  useSmoothWheel(editorRootRef);
  useEditorScrollChrome(editorRootRef);
  useInlineCodeShortcut(editorRootRef);
  useLineStartKeys({
    editorRootRef,
    blocksRef,
    onUpdate: handleUpdate,
    onMergeWithPrevious: handleMergeWithPrevious,
  });

  // Arm undo so Ctrl+Z works after toolbar actions even if focus was outside
  // the editor (toolbar is portaled into page chrome).
  const handleUncheckAll = useCallback(() => {
    undoArmedRef.current = true;
    uncheckAll();
  }, [uncheckAll, undoArmedRef]);

  const handleEmptyVariableValues = useCallback(() => {
    undoArmedRef.current = true;
    emptyVariableValues();
  }, [emptyVariableValues, undoArmedRef]);

  const docFind = useDocFind(editorRootRef, blocks, blocksRef, commit);

  const {
    activeId,
    activeBlock,
    dropTarget,
    fileDragActive,
    sensors,
    handleDragStart,
    handleDragEnd,
    handleDragCancel,
    updateFileDropTarget,
    clearFileDropTarget,
    peekDropTarget,
  } = useDragAndDrop(blocksRef, commit, (id) => {
    undoArmedRef.current = true;
    setSelectedBlockId(id);
  });

  useMediaFileDrop(editorRootRef, blocksRef, commit, setSelectedBlockId, {
    updateFileDropTarget,
    clearFileDropTarget,
    peekDropTarget,
  });

  const dragModifiers = useMemo(
    () =>
      activeBlock?.type === 'image' || activeBlock?.type === 'video'
        ? [snapOverlayCenterToCursor]
        : [],
    [activeBlock?.type]
  );

  const toolbar = (
    <DocEditorToolbar
      onToggleInlineCode={toggleInlineCode}
      onUncheckAll={handleUncheckAll}
      canUncheckAll={anyChecked}
      onEmptyVariableValues={handleEmptyVariableValues}
      canEmptyVariableValues={canEmptyVariableValues}
      onFind={docFind.open}
    />
  );

  return (
    <div className="doc-editor" ref={editorRootRef} data-doc-id={docId || undefined}>
      {toolbarSlot ? createPortal(toolbar, toolbarSlot) : toolbar}
      <DocFindBar
        editorRootRef={editorRootRef}
        isOpen={docFind.isOpen}
        query={docFind.query}
        onQueryChange={docFind.setQuery}
        matchCount={docFind.matches.length}
        activeIndex={docFind.activeIndex}
        inputRef={docFind.inputRef}
        barRef={docFind.barRef}
        onClose={docFind.close}
        onNext={docFind.goNext}
        onPrev={docFind.goPrev}
      />
      <DocMinimap editorRootRef={editorRootRef} />
      {videoPasteChoice}
      <DndContext
        sensors={sensors}
        modifiers={dragModifiers}
        onDragStart={handleDragStart}
        onDragEnd={handleDragEnd}
        onDragCancel={handleDragCancel}
      >
        {/* eslint-disable-next-line jsx-a11y/no-static-element-interactions, jsx-a11y/click-events-have-key-events */}
        <div
          className="doc-editor-blocks"
          onMouseDown={handleCanvasMouseDown}
          onClick={handleCanvasClick}
        >
          {blocks.map((block, i) => (
            <BlockRenderer
              key={block.id}
              block={block}
              prevType={blocks[i - 1]?.type}
              nextType={blocks[i + 1]?.type}
              listNumber={
                block.type === 'number' ? getNumberedListIndex(blocks, i) : undefined
              }
              onUpdate={handleUpdate}
              onInsertAfter={handleInsertAfter}
              onUpdateAndInsert={handleUpdateAndInsert}
              onDelete={handleDelete}
              onMergeWithPrevious={handleMergeWithPrevious}
              onMergeWithNext={handleMergeWithNext}
              onNavigate={handleNavigate}
              onEscapeContainer={handleEscapeContainer}
              onWrapInCallout={onWrapInCallout}
              onUnwrapCallout={onUnwrapCallout}
              onOpenDoc={onOpenDoc}
              resolveDocMeta={resolveDocMeta}
              availableDocs={availableDocs}
              snippetBacklinks={snippetBacklinks}
              resolveVariable={resolveVariable}
              availableVariables={availableVariables}
              onCreateVariable={handleCreateVariable}
              hasVariablesBlock={hasVariablesBlock}
              docVariables={docVariables}
              onDocVariablesChange={
                onDocVariablesChange ? handleDocVariablesChange : null
              }
              onRenameVariable={
                onDocVariablesChange ? handleRenameVariable : null
              }
              variablesHistoryEpoch={variablesHistoryEpoch}
              variableCampaign={variableCampaign}
              allSprints={allSprints}
              onCampaignVariablesChange={onCampaignVariablesChange}
              missionVariables={missionVariables}
              onMissionVariablesChange={onMissionVariablesChange}
              onPatchInheritedVariable={onPatchInheritedVariable}
              variablesColumnWidths={variablesColumnWidths}
              onVariablesColumnWidthsChange={onVariablesColumnWidthsChange}
              tickOverrides={tickOverrides}
              onTickChange={onTickChange}
              fileOverrides={fileOverrides}
              onFilesChange={onFilesChange}
              missionId={missionId}
              docId={docId}
              activeId={activeId}
              fileDragActive={fileDragActive}
              dropTarget={dropTarget}
              selectedBlockId={selectedBlockId}
              onSelect={setSelectedBlockId}
              multiSelectedIds={multiSelected}
            />
          ))}
          <BottomDropSpace />
        </div>

        <DragOverlay>
          <BlockDragOverlay activeBlock={activeBlock} />
        </DragOverlay>
      </DndContext>

      <ConfirmModal
        isOpen={!!deleteConfirm}
        title={deleteConfirm?.title || 'Delete referenced block?'}
        message={deleteConfirm?.message || ''}
        confirmLabel="Delete"
        cancelLabel="Cancel"
        confirmVariant="danger"
        onConfirm={() => finishDeleteConfirm(true)}
        onCancel={() => finishDeleteConfirm(false)}
      />
    </div>
  );
};

export { createBlock };
